import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { requireAdmin } from '@/features/auth/guards'
import { landingRouteFor } from '@/features/auth/guards'
import { useMe } from '@/features/auth/hooks'
import { EmployeesTab } from '@/features/employees/components/employees-tab'
import { UsersTab } from '@/features/users/components/users-tab'
import { RolesTab } from '@/features/roles/components/roles-tab'
import { SSOTab } from '@/features/sso/components/sso-tab'
import { TemplatesTab } from '@/features/messaging/components/templates-tab'
import { DocumentTemplatesTab } from '@/features/documents/components/templates-tab'
import { SuppliersTab } from '@/features/parts/suppliers-tab'
import { IntegrationsTab } from '@/features/integrations/components/integrations-tab'

const SETTINGS_TABS = [
  'employees',
  'users',
  'roles',
  'sso',
  'templates',
  'document-templates',
  'suppliers',
  'integrations',
] as const
type SettingsTab = (typeof SETTINGS_TABS)[number]

function isSettingsTab(value: unknown): value is SettingsTab {
  return typeof value === 'string' && (SETTINGS_TABS as readonly string[]).includes(value)
}

export const Route = createFileRoute('/_authenticated/settings/account')({
  beforeLoad: requireAdmin,
  // The active tab rides in the URL so a refresh or shared link restores it.
  validateSearch: (search: Record<string, unknown>): { tab?: SettingsTab } => ({
    tab: isSettingsTab(search.tab) ? search.tab : undefined,
  }),
  component: AccountSettingsPage,
})

function AccountSettingsPage() {
  const { tab } = Route.useSearch()
  const navigate = useNavigate({ from: '/settings/account' })
  const { data: me } = useMe()
  const activeTab: SettingsTab = tab ?? 'employees'

  const setActiveTab = (next: string) => {
    if (!isSettingsTab(next)) return
    navigate({ search: { tab: next === 'employees' ? undefined : next } })
  }

  return (
    <div className="container mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          {/* Back to wherever the admin came from (dashboard for admins). */}
          <Link to={me ? (landingRouteFor(me) as never) : ('/' as never)}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Account Settings</h1>
          <p className="text-muted-foreground text-sm">
            Staff roster, sign-in access, message templates, and account-wide options.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="sso">Sign-in</TabsTrigger>
          <TabsTrigger value="templates">Message Templates</TabsTrigger>
          <TabsTrigger value="document-templates">Document Templates</TabsTrigger>
          <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>
        <TabsContent value="employees">
          <EmployeesTab />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
        <TabsContent value="roles">
          <RolesTab />
        </TabsContent>
        <TabsContent value="sso">
          <SSOTab />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="document-templates">
          <DocumentTemplatesTab />
        </TabsContent>
        <TabsContent value="suppliers">
          <SuppliersTab />
        </TabsContent>
        <TabsContent value="integrations">
          <IntegrationsTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
